import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { Observable } from 'rxjs';
import { HotspotResultDTO, StudentClosedPollDTO, StudentPollStateDTO, StudentWidgetDTO } from '../../models/widgets/student-widget.interface';
import { WidgetEventTypeEnum } from '../../models/widgets/widget-event-type.enum';

// providedIn: 'root' schválně — StudentCompanyCourseFacade je root-provided a služba
// zaregistrovaná jen v CourseDomainModule by z ní byla neviditelná (NullInjectorError
// za běhu, build i testy projdou). Stejně je na tom sousední StudentCompanyCourseService.
@Injectable({ providedIn: 'root' })
export class StudentWidgetService {

  protected baseUrl = `${webApiUrl}/student/Widget`;

  constructor(
    private http: HttpClient,
  ) { }

  /**
   * ⚠ Bere **articleMetaId**, ne id článku — studentské routy pracují s jazykovou
   * mutací a záměna je tichá 404.
   */
  getForArticle(articleMetaId: string, url: string = `${webApiUrl}/student/Article/${articleMetaId}/Widget`): Observable<StudentWidgetDTO[]> {
    return this.http.get<StudentWidgetDTO[]>(url, { headers });
  }

  /**
   * Ankety kurzu, které se uzavřely, student v nich hlasoval a výsledek ještě neviděl.
   *
   * ⚠ Bere **courseMetaId**, ne id kurzu — stejně jako zbytek studentských rout.
   */
  getClosedPolls(courseMetaId: string, url: string = `${webApiUrl}/student/Course/${courseMetaId}/ClosedPoll`): Observable<StudentClosedPollDTO[]> {
    return this.http.get<StudentClosedPollDTO[]>(url, { headers });
  }

  trackEvent(widgetId: string, eventType: WidgetEventTypeEnum, url: string = `${this.baseUrl}/${widgetId}/event`): Observable<void> {
    return this.http.post<void>(url, { eventType }, { headers });
  }

  /**
   * Táž událost pro víc widgetů jedním requestem.
   *
   * ⚠ Otevření článku hlásí zobrazení za každý blok. Po jednom to znamenalo tolik
   * requestů, kolik má článek částí — a každý z nich na serveru znovu dohledával
   * zápis studenta přes celý strom kurzu.
   */
  trackEvents(widgetIds: string[], eventType: WidgetEventTypeEnum, url: string = `${this.baseUrl}/events`): Observable<void> {
    return this.http.post<void>(url, { widgetIds, eventType }, { headers });
  }

  /**
   * Hlas v anketě. Vrací **nový stav** — do uzavření bez čísel, po uzavření s nimi.
   * Klient si tedy nic nedopočítává a nemá co skrývat.
   */
  /**
   * Hlas v anketě. Posílá se **seznam** — anketa smí přijmout víc voleb (EL-2187).
   * Server bere volbu jako celek: co v seznamu není, student už zvolené nemá.
   */
  /**
   * Stav jedné ankety bez otevírání článku (EL-2204).
   *
   * ⚠ Kolik toho v odpovědi bude, rozhoduje server: čísla pošle jen tomu, kdo hlasoval
   * (anketa bez uzavření), nebo po termínu (anketa s uzavřením).
   */
  getPollState(widgetId: string, url: string = `${this.baseUrl}/${widgetId}/poll`): Observable<StudentPollStateDTO> {
    return this.http.get<StudentPollStateDTO>(url, { headers });
  }

  vote(widgetId: string, optionKeys: string[], url: string = `${this.baseUrl}/${widgetId}/poll`): Observable<StudentPollStateDTO> {
    return this.http.post<StudentPollStateDTO>(url, { optionKeys }, { headers });
  }

  /** Stažení vlastního hlasu. Po uzavření ankety server odmítne (400). */
  retractVote(widgetId: string, url: string = `${this.baseUrl}/${widgetId}/poll`): Observable<StudentPollStateDTO> {
    return this.http.delete<StudentPollStateDTO>(url, { headers });
  }

  /**
   * Kliknutí do klikacího obrázku.
   *
   * ⚠ V režimu hledání chodí text oblasti **až odsud**. S widgetem nepřijde, protože
   * by prozradil, kde je chyba (stejný důvod jako u kvízu v EL-2024).
   */
  clickHotspot(widgetId: string, spotId: string, url: string = `${this.baseUrl}/${widgetId}/hotspot`): Observable<HotspotResultDTO> {
    return this.http.post<HotspotResultDTO>(url, { spotId }, { headers });
  }
}
